<template>
    <ToastRoot v-slot="{ remaining }" class="toastRoot" :class="[classes.toastRoot, classes[themeClasses[theme]]]" :duration="duration">
        <span v-if="iconClasses[theme]" :class="classes.icon" aria-hidden="true">
            <slot name="icon"><i class="bi" :class="iconClasses[theme]" aria-hidden="true"></i></slot>
        </span>
        <ToastDescription class="grow" :class="classes.description">
            <slot name="description">
                <p class="max-w-[320px] mb-1">
                    {{
                        truncate(description, {
                            length: truncateLength,
                            omission: " ...",
                        })
                    }}
                </p>
            </slot>
        </ToastDescription>
        <ToastAction v-if="actionText || $slots.action" :as-child="asChild" :alt-text="actionText" :class="classes.action" @click="$emit('action')">
            <slot name="action">
                <BaseButton theme="tertiary" class="scaffold-body-small-medium underline">{{ actionText }}</BaseButton>
            </slot>
        </ToastAction>
        <ToastClose :as-child="asChild" :class="classes.close">
            <slot name="close">
                <button type="button" aria-label="Close" class="bi bi-x-lg leading-none"></button>
            </slot>
        </ToastClose>
        <div
            class="absolute transition-[width]"
            :class="[remaining === duration ? 'rounded-b-full' : 'rounded-bl-full', classes.durationBar]"
            :style="{
                width: `${(remaining / duration) * 100}%`,
            }"></div>
    </ToastRoot>
</template>

<script setup lang="ts">
import truncate from "lodash/truncate";
import { ToastAction, ToastClose, ToastDescription, ToastRoot } from "reka-ui";

import BaseButton from "./BaseButton.vue";

type BaseToastTheme = "primary" | "error" | "info" | "warning" | "success";

type BaseToastProps = {
    theme?: BaseToastTheme;
    asChild?: boolean;
    duration?: number;
    description?: string;
    actionText?: string;
    truncateLength?: number;
};

const { theme = "primary", duration = 5000, asChild = true, description = "", actionText = "", truncateLength = 100 } = defineProps<BaseToastProps>();

const themeClasses: Record<BaseToastTheme, string> = {
    primary: "themePrimary",
    error: "themeError",
    info: "themeInfo",
    warning: "themeWarning",
    success: "themeSuccess",
};

const iconClasses: Record<BaseToastTheme, string | null> = {
    primary: null,
    error: "bi-x-circle-fill",
    info: "bi-info-circle-fill",
    warning: "bi-exclamation-circle-fill",
    success: "bi-check-circle-fill",
};

defineEmits<{
    action: [];
}>();
</script>

<style module="classes">
@reference "../../../css/tailwind.css";

@layer components {
    .toastRoot {
        @apply data-[state=open]:animate-slide-from-bottom data-[state=closed]:animate-hide ml-4 my-1 px-5 py-1 flex gap-2 items-center rounded-lg relative border;
    }

    .description {
        @apply body-small;
    }

    .icon {
        @apply hidden;
    }

    :global(html[data-theme="tonal"]) .toastRoot {
        @apply px-4 py-2.5 gap-3 rounded-md shadow-(--shadow-toast);
    }

    :global(html[data-theme="tonal"]) .toastRoot .description {
        @apply scaffold-body-small;
    }

    :global(html[data-theme="tonal"]) .toastRoot .icon {
        @apply inline-flex shrink-0 text-base/none;
    }

    /* Three classes deep so the action colour also beats BaseButton's own
       hover/disabled text rules, as the utility on the action did before. */
    .toastRoot.themePrimary {
        @apply bg-toast-primary-bg border-toast-primary-border;
    }

    .toastRoot.themePrimary .description,
    .toastRoot.themePrimary .action,
    .toastRoot.themePrimary .close,
    .toastRoot.themePrimary .icon {
        @apply text-toast-primary-text;
    }

    .toastRoot.themePrimary .durationBar {
        @apply border-toast-primary-progress;
    }

    .toastRoot.themeError {
        @apply bg-toast-error-bg border-toast-error-border;
    }

    .toastRoot.themeError .description,
    .toastRoot.themeError .action,
    .toastRoot.themeError .close,
    .toastRoot.themeError .icon {
        @apply text-toast-error-text;
    }

    .toastRoot.themeError .durationBar {
        @apply border-toast-error-border;
    }

    .toastRoot.themeInfo {
        @apply bg-toast-info-bg border-toast-info-border;
    }

    .toastRoot.themeInfo .description,
    .toastRoot.themeInfo .action,
    .toastRoot.themeInfo .close,
    .toastRoot.themeInfo .icon {
        @apply text-toast-info-text;
    }

    .toastRoot.themeInfo .durationBar {
        @apply border-toast-info-border;
    }

    .toastRoot.themeWarning {
        @apply bg-toast-warning-bg border-toast-warning-border;
    }

    .toastRoot.themeWarning .description,
    .toastRoot.themeWarning .action,
    .toastRoot.themeWarning .close,
    .toastRoot.themeWarning .icon {
        @apply text-toast-warning-text;
    }

    .toastRoot.themeWarning .durationBar {
        @apply border-toast-warning-border;
    }

    .toastRoot.themeSuccess {
        @apply bg-toast-success-bg border-toast-success-border;
    }

    .toastRoot.themeSuccess .description,
    .toastRoot.themeSuccess .action,
    .toastRoot.themeSuccess .close,
    .toastRoot.themeSuccess .icon {
        @apply text-toast-success-text;
    }

    .toastRoot.themeSuccess .durationBar {
        @apply border-toast-success-border;
    }

    .durationBar {
        @apply duration-75 bottom-0 left-0 border-b-4;
    }
}
</style>
